/**
 * The enrolment QR.
 *
 * `qrcode-generator` is 4 KB and has no dependencies of its own. ARCH §7.3 asks
 * for headless primitives with hand-written visuals, and this is the same
 * bargain: the library does the Reed–Solomon encoding, and the SVG below is
 * ours, in tokens, so the code sits on `--mdx-white` with `--mdx-ink` modules
 * like everything else.
 *
 * SVG rather than a canvas — it prints, it scales, and it needs no ref.
 *
 * Stories 4.4 and 24.3.
 */
import qrcode from 'qrcode-generator'

export function QrCode({ value, label }: { value: string; label: string }) {
  // Type 0 = smallest that fits; 'M' = 15% recovery, the level every
  // authenticator app's own docs assume.
  const qr = qrcode(0, 'M')
  qr.addData(value)
  qr.make()

  const count = qr.getModuleCount()
  const path: string[] = []
  for (let row = 0; row < count; row++) {
    for (let column = 0; column < count; column++) {
      if (qr.isDark(row, column)) path.push(`M${column} ${row}h1v1h-1z`)
    }
  }

  return (
    /**
     * In a tile, as `settings-profile.html`'s `enrol-scan` draws it: 176px square,
     * a 1px line, the control radius and 12px of white inside (Story 24.3). The
     * padding and the one-module margin below make the quiet zone together —
     * about four modules, which is what the spec asks for.
     */
    <span className="flex size-mdx-qr shrink-0 rounded-mdx-control border border-mdx-line bg-mdx-white p-mdx-12">
      <svg role="img" aria-label={label} viewBox={`-1 -1 ${count + 2} ${count + 2}`} className="size-full">
        {/* The quiet zone is part of the spec — a scanner needs the margin. */}
        <rect x="-1" y="-1" width={count + 2} height={count + 2} fill="var(--mdx-white)" />
        <path d={path.join('')} fill="var(--mdx-ink)" />
      </svg>
    </span>
  )
}
